import * as SelectPrimitive from '@radix-ui/react-select'
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import { CaretDown, CaretUp, CaretUpDown, Check } from '@/components/icons'
import { cn } from '@/lib/cn'

export const Select = SelectPrimitive.Root
export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

export const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: 'sm' | 'default'
    /**
     * `text` drops the field chrome and renders the value as underlined inline
     * text with a double-caret, for places where a boxed control would be too
     * heavy (a table footer's rows-per-page picker).
     */
    variant?: 'default' | 'text'
  }
>(function SelectTrigger(
  { className, size = 'default', variant = 'default', children, ...props },
  ref,
) {
  if (variant === 'text') {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        data-size={size}
        data-variant="text"
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded-sm text-[12px] font-medium text-[color:var(--foreground)]',
          'underline decoration-[color:var(--input)] decoration-1 underline-offset-[3px] hover:decoration-[color:var(--foreground)]',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <CaretUpDown className="size-3.5 shrink-0 text-[color:var(--muted-foreground)]" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  }
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      data-size={size}
      className={cn(
        'inline-flex items-center justify-between gap-1.5 whitespace-nowrap rounded-md bg-[color:var(--card)] px-2.5 text-[12.5px] text-[color:var(--foreground)]',
        'shadow-[var(--shadow-raised)] ring-1 ring-inset ring-[color:var(--border)]',
        'hover:bg-[color:var(--muted)]/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[color:var(--ring)]',
        'disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-[color:var(--muted-foreground)]',
        size === 'sm' ? 'h-7 max-md:min-h-11' : 'h-8 max-md:min-h-11',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <CaretDown className="size-3 shrink-0 text-[color:var(--muted-foreground)]" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})

export const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent(
  { className, children, position = 'popper', sideOffset = 4, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(
          'bf-popover-motion relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-md',
          'bg-[color:var(--popover)] text-[13px] text-[color:var(--popover-foreground)] shadow-[var(--shadow-overlay)]',
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
          <CaretUp className="size-3" />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
          <CaretDown className="size-3" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})

export const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(function SelectLabel({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Label
      ref={ref}
      className={cn(
        'px-2 py-1 text-[10.5px] uppercase tracking-wider text-[color:var(--muted-foreground)]',
        className,
      )}
      {...props}
    />
  )
})

export const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ className, children, ...props }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-7 text-[12.5px] outline-none',
        'focus:bg-[color:var(--muted)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-3.5 text-[color:var(--primary)]" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
})

export const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(function SelectSeparator({ className, ...props }, ref) {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-[color:var(--border)]', className)}
      {...props}
    />
  )
})
